import type { RequestOptions } from "../common/server";
import type { ListJs } from "@/common/models";
import type { AccountRowJs, AddSeatsToSubscriptionResult, ChangeGuestsToMembersRequest, CloudProduct, CloudSubscriptionInfo, CreateOrganizationRequest, CreateOrganizationResponse, OrganizationJs, OrganizationSubscriptionsInfo, ResponseObject, SeatsAndLicensesInfo, SharedFilesystemMappingJs, ShortcutStatusJs, SubInstanceCloudPlusStatus, SubscriptionPurchaseInfo, UserCloudRole, UserType } from "@/generated/models";
import { objectToFormData } from "@/services/utils/formData";
import { server } from "./server";

export async function getAccounts({ organizationId }: { organizationId: string }, { signal }: RequestOptions = {}) {
  const response = await server.get("organization/getAccounts", {
    searchParams: {
      organizationId,
    },
    signal,
  });
  return await response.json() as ListJs<AccountRowJs>;
}

export async function getOrganization({ organizationId }: { organizationId: string }, { signal }: RequestOptions = {}) {
  const response = await server.get("organization/getUserOrganization", {
    searchParams: {
      organizationId,
    },
    signal,
  });
  return await response.json() as OrganizationJs;
}

export async function getOrganizations({ signal }: RequestOptions = {}) {
  const response = await server.get("organization/getUserOrganizations", {
    signal,
  });
  return await response.json() as OrganizationJs[];
}

export async function createOrganization(request: CreateOrganizationRequest) {
  const formData = objectToFormData(request);
  const response = await server.post("organization/createOrganization", { body: formData });
  return await response.json() as CreateOrganizationResponse;
}

export async function getFilesystemMappings({ organizationId }: { organizationId: string }, { signal }: RequestOptions = {}) {
  const response = await server.get("organization/getFilesystemMappingsForOrganization", {
    searchParams: {
      organizationId,
    },
    signal,
  });
  return await response.json() as SharedFilesystemMappingJs[];
}

interface OrganizationLeaveModel {
  organizationId: string;
  newOwner?: string;
}
export async function leaveOrganization(options: { organizationId: string; newOwner?: string }) {
  const model: OrganizationLeaveModel = {
    organizationId: options.organizationId,
    newOwner: options.newOwner,
  };
  const formData = objectToFormData(model);
  const response = await server.post("organization/leaveOrganization", { body: formData });
  return await response.json() as OrganizationJs;
}

export interface RemoveUsersFromOrganizationModel {
  usersToRemoveIds: string;
  organizationId: string;
}

export async function removeUsersFromOrganization(options: { userIds: string[]; organizationId: string }) {
  const model: RemoveUsersFromOrganizationModel = {
    usersToRemoveIds: options.userIds.join(","),
    organizationId: options.organizationId,
  };

  const formData = objectToFormData(model);
  const response = await server.post("organization/removeUsersFromOrganization", { body: formData });
  return await response.json() as ResponseObject;
}

export interface InviteUsersToOrganizationModel {
  userEmails: string;
  organizationId: string;
  organizationRole: number;
  cloudRole: number;
  recaptchaToken?: string;
}

export async function inviteUsersToOrganization(options: { organizationId: string; userEmails: string[]; organizationRole: UserType; cloudRole: UserCloudRole; recaptchaToken?: string }) {
  const model: InviteUsersToOrganizationModel = {
    userEmails: options.userEmails.join(","),
    organizationId: options.organizationId,
    organizationRole: options.organizationRole,
    cloudRole: options.cloudRole,
    recaptchaToken: options.recaptchaToken,
  };

  const formData = objectToFormData(model);
  const response = await server.post("organization/inviteUsersToOrganization", { body: formData });
  return await response.json() as ResponseObject;
}

export interface RenameOrganizationModel {
  organizationId: string;
  name: string;
}

export async function renameOrganization(options: { organizationId: string; name: string }) {
  const model: RenameOrganizationModel = {
    organizationId: options.organizationId,
    name: options.name,
  };

  const formData = objectToFormData(model);
  const response = await server.post("organization/renameOrganization", { body: formData });
  return await response.json() as ResponseObject;
}

interface OrganizationDeleteModel {
  organizationId: string;
}
export async function deleteOrganization({ organizationId }: { organizationId: string }) {
  const model: OrganizationDeleteModel = {
    organizationId,
  };
  const formData = objectToFormData(model);
  const response = await server.post("organization/deleteOrganization", { body: formData });
  return await response.json() as OrganizationJs;
}

export interface SetUserCloudRoleModel {
  organizationId: string;
  userIds: string;
  cloudRole: number;
}

export async function setCloudRole(options: { organizationId: string; userIds: string[]; cloudRole: UserCloudRole }) {
  const model: SetUserCloudRoleModel = {
    organizationId: options.organizationId,
    userIds: options.userIds.join(","),
    cloudRole: options.cloudRole,
  };

  const formData = objectToFormData(model);
  const response = await server.post("organization/setCloudRole", { body: formData });
  return await response.json() as ResponseObject;
}

export async function getUserOrganizationsSubscriptionsInfo({ signal }: RequestOptions = {}) {
  const response = await server.get("organization/GetUserOrganizationsSubscriptionsInfo", {
    signal,
  });
  return await response.json() as OrganizationSubscriptionsInfo[];
}

export async function getOrganizationSubscriptions({ organizationId }: { organizationId: string }, { signal }: RequestOptions = {}) {
  const response = await server.get("organization/getSubscriptions", {
    searchParams: {
      organizationId,
    },
    signal,
  });
  return await response.json() as CloudSubscriptionInfo[];
}

export async function getSeatsAndLicensesInfo({ organizationId }: { organizationId: string }, { signal }: RequestOptions = {}) {
  const response = await server.get("organization/GetSeatsAndLicensesInfo", {
    searchParams: {
      organizationId,
    },
    signal,
  });
  return await response.json() as SeatsAndLicensesInfo;
}

export interface SetCloudAuthorSeat {
  userToChangeId: string;
  organizationId: string;
  assignSeat: boolean;
}

export async function setCloudAuthorSeat(options: SetCloudAuthorSeat) {
  const formData = objectToFormData(options);
  const response = await server.post("organization/setCloudAuthorSeat", { body: formData });
  return await response.json() as ResponseObject;
}

export interface SetCloudAuthorSeatsBulkParameters {
  userIds: string;
  organizationId: string;
  assignSeats: boolean;
}

export async function setCloudAuthorSeatsBulk(options: SetCloudAuthorSeatsBulkParameters) {
  const formData = objectToFormData(options);
  const response = await server.post("organization/setCloudAuthorSeatsBulk", { body: formData });
  return await response.json() as ResponseObject;
}

export async function assignSubscription({ organizationId, subscriptionCode }: { organizationId: string; subscriptionCode: string }) {
  const model = {
    organizationId,
    subscriptionCode,
  };

  const formData = objectToFormData(model);
  const response = await server.post("organization/AssignSubscription", { body: formData });
  return await response.json() as ResponseObject;
}

export async function changeGuestsToMembers({ organizationId, request }: { organizationId: string; request: ChangeGuestsToMembersRequest }) {
  const formData = objectToFormData({
    organizationId,
    request,
  });
  const response = await server.post("organization/ChangeGuestsToMembers", { body: formData });
  return await response.json() as ResponseObject;
}

export async function setSubscriptionManagement({ organizationId, subscriptionId, adminManaged }: { organizationId: string; subscriptionId: string; adminManaged: boolean }) {
  const formData = objectToFormData({
    organizationId,
    subscriptionId,
    adminManaged,
  });
  const response = await server.post("organization/SetSubscriptionAdminManagement", { body: formData });
  return await response.json() as ResponseObject;
}

export async function cancelSubscriptionSeats({ organizationId, subscriptionId, cloudAuthorCanceled, rpPublisherCanceled }: { organizationId: string; subscriptionId: string; cloudAuthorCanceled: number; rpPublisherCanceled: number }) {
  const formData = objectToFormData({
    organizationId,
    subscriptionId,
    cloudAuthorCanceled,
    rpPublisherCanceled,
  });
  const response = await server.post("organization/CancelSubscriptionSeats", { body: formData });
  return await response.json() as ResponseObject;
}

export async function getSubscriptionPurchaseInfo({ organizationId, subscriptionId }: { organizationId: string; subscriptionId: string }, { signal }: RequestOptions = {}) {
  const response = await server.get("organization/GetSubscriptionPurchaseInfo", {
    searchParams: {
      organizationId,
      subscriptionId,
    },
    signal,
  });
  return await response.json() as SubscriptionPurchaseInfo;
}

export async function addSeatsToSubscription({ organizationId, subscriptionId, products }: { organizationId: string; subscriptionId: string; products: CloudProduct[] }) {
  const formData = objectToFormData({
    organizationId,
    subscriptionId,
    products,
  });
  const response = await server.post("organization/AddSeatsToSubscription", { body: formData });
  return await response.json() as AddSeatsToSubscriptionResult;
}

export async function getStatuses({ signal }: RequestOptions = {}) {
  const response = await server.get("organization/getStatuses", {
    signal,
  });
  return await response.json() as ShortcutStatusJs[];
}

export async function getCloudPlusStatus({ organizationId }: { organizationId: string }, { signal }: RequestOptions = {}) {
  const response = await server.get("organization/GetOrganizationCloudPlusStatus", {
    searchParams: {
      organizationId,
    },
    signal,
  });
  return await response.json() as SubInstanceCloudPlusStatus;
}
